import type {RouteLocationNormalized, Router} from 'vue-router';
import {addGtag, configure, set} from 'vue-gtag';

export const FLEETWIRE_MEASUREMENT_ID = 'G-NWFRX8HJBK';

/**
 * Customer-facing first-path-segments. Mirrors STOREFRONT_PREFIXES in
 * app/Support/SpaPathAllowlist.php — keep the two lists in sync.
 */
const STOREFRONT_PREFIXES = new Set(['invoice', 'pay', 'public', 'rental', 'signing']);

export type SiteArea = 'dashboard' | 'storefront';

/**
 * Whether to flag hits for GA4 DebugView.
 *
 * Deliberately a runtime host check, not `import.meta.env`: assets are built
 * locally and the artifacts are deployed as-is, so any build-time flag (MODE,
 * VITE_APP_ENV) is baked from the developer's own .env and would ship to
 * production. Herd serves local dev on `.test`, which prod/staging never use.
 */
export function isDebugHost(hostname: string = window.location.hostname): boolean {
    return hostname === 'localhost' || hostname.endsWith('.test');
}

export function resolveSiteArea(path: string): SiteArea {
    const firstSegment = path.split('?')[0].split('/').filter(Boolean)[0] ?? '';

    return STOREFRONT_PREFIXES.has(firstSegment) ? 'storefront' : 'dashboard';
}

export function initGtag(router: Router): void {
    const siteArea = resolveSiteArea(window.location.pathname);

    configure({
        tagId: FLEETWIRE_MEASUREMENT_ID,
        initMode: 'manual',
        config: {
            send_page_view: false,
            site_area: siteArea,
            content_group: siteArea,
            ...(isDebugHost() ? {debug_mode: true} : {}),
        },
        pageTracker: {
            router,
            useRouteFullPath: true,
            template: (to: RouteLocationNormalized) => {
                const area = resolveSiteArea(to.path);

                return {
                    page_path: to.fullPath,
                    ...(typeof to.name === 'string' ? {page_title: to.name} : {}),
                    site_area: area,
                    content_group: area,
                };
            },
        },
    });

    router.afterEach((to, from) => {
        if (to.path !== from.path) {
            const area = resolveSiteArea(to.path);
            set({site_area: area, content_group: area});
        }
    });

    addGtag().catch(() => {
        console.warn('Google Analytics failed to load');
    });
}
